import "./modules/dropdown"
import "./modules/toast"
import "./modules/filters"
import { initTooltips } from "./modules/tooltip"
import "./modules/select-searchable"
import { loader } from "./modules/loader"
import sentry from "./modules/sentry"
import { initTextEllipsis } from "../../../src/client/scripts/modules/text-ellipsis"
import { BUTTON_DEBOUNCE } from "./modules/form"
import { formatMarkdown, addPreCopyButtons } from "./modules/markdown"
import "./modules/textarea"
import "./modules/add-user-to-organization"
import "./modules/drag-sort"
import MonacoEditorManager from "./modules/monaco-editor"
import "./modules/fileUploader"
import "./modules/global-settings-assets-uploader"
import "./modules/sidebar/index"
import "./modules/chat/index"
import "./modules/page-sse"
import { match } from "ts-pattern"
import { generateToken } from "./modules/turnstile"
import "./modules/checkbox"
import "./modules/pages-sidebar/index"
import { initCardGlow } from "./modules/card-glow"
import "./modules/auth"
import "./modules/oauth-consent"

declare global {
  interface Window {
    togglePasswordInput: (event: MouseEvent) => void
    copyToClipboard: (
      event: MouseEvent,
      value: string,
      onSuccess: () => void,
    ) => void
    toggleCopyIcon: (element: HTMLElement, timeout?: number, iconName?: string) => void
    showTemporaryTooltip: (anchor: HTMLElement, text: string, duration?: number) => void
    APP_BASE_URL: string
    closeModal: (eventOrId: MouseEvent | string, remove?: boolean) => void
    // SENTRY
    SENTRY_DSN: string | null
    ENVIRONMENT: string
    SENTRY_VERSION: string | null
    loader: {
      show: () => void
      hide: () => void
      isReady: () => boolean
    }

    addCustomerBodyClass: () => void
    addStaffBodyClass: () => void
    MAX_FILE_SIZE: number
  }
  interface HTMLElementEventMap {
    "htmx:beforeRequest": CustomEvent
    "htmx:historyRestore": CustomEvent
  }
}

window.loader = loader

const setFooterHeight = () => {
  const inputPrompt = document.getElementById("input-prompt-inner-container")
  if (inputPrompt && document.activeElement !== inputPrompt) {
    inputPrompt.dispatchEvent(new Event("input"))
    const form = inputPrompt.closest("form")
    if (form) {
      const height = form.getBoundingClientRect().height
      form.style.setProperty("min-height", `${height}px`)
    }
  }
  
  const footer = document.querySelector("footer") as HTMLElement
  if (footer) {
    const footerHeight = footer.offsetHeight
    document.documentElement.style.setProperty(
      "--footer-height",
      `${footerHeight}px`,
    )
  }
  
  let homePageGap = "32px"
  if (window.innerWidth < 768) {
    homePageGap = "20px"
  }

  document.documentElement.style.setProperty(
    "--home-page-gap",
    homePageGap,
  )
}

const setViewportHeight = () => {
  const height = window.visualViewport?.height ?? window.innerHeight
  document.documentElement.style.setProperty('--viewport-height', `${height}px`)
}

window.visualViewport?.addEventListener('resize', () => {
  const focusedEl = document.activeElement

  const isInput = focusedEl instanceof HTMLTextAreaElement
  const isPromptInput = isInput && focusedEl.getAttribute("id") === "input-prompt-inner-container"

  const keyboardOpen = isPromptInput && window.visualViewport 
    ? window.visualViewport.height < window.screen.height * 0.75 
    : false

  document.querySelector("footer")?.classList.toggle("!hidden", keyboardOpen)
  setFooterHeight()
  setViewportHeight()
})

window.visualViewport?.addEventListener('scroll', () => {
  if (document.querySelector(".chat__messages__list")) {
    window.scrollTo(0, 0)
  }
}, { passive: true })

const restartSpinnersAnimations = () => {
  const els = document.querySelectorAll("animateTransform")
  els.forEach((element) => {
    const clone = element.cloneNode(true) as SVGElement
    element.parentNode?.replaceChild(clone, element)
  })
}

window.addEventListener("focus", () => {
  window.initSSE()
  window.initPageSSE()
})

window.addEventListener("blur", () => {
  const html = document.documentElement
  if (!html) {
    return
  }

  html.removeAttribute("data-chat-sse-initialized")
})

document.addEventListener("htmx:confirm", async(event) => {  
  event.preventDefault()
  
  const question = (event as CustomEvent).detail.question as string | undefined
  
  await match(question)
    .with("turnstileVerification", async() => {
      if (!window.turnstileToken.value && !window.turnstileToken.loading) {
        await generateToken()
      }

      while (window.turnstileToken.loading) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      (event as CustomEvent).detail.issueRequest({ skipConfirmation: true })
    })
    .otherwise(() => {
      (event as CustomEvent).detail.issueRequest()
    });
})

window.addEventListener("DOMContentLoaded", () => {
  console.log(`%c Polarity Website `, "background: #000; color: #fff; padding: 4px 8px; border-radius: 4px;")

  void(async () => {
    if (window.RECAPTCHA_ACTIVE) {
      while (!window.turnstile) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      generateToken()
    }
  })()
  
  setViewportHeight()

  initTooltips()
  sentry.init()
  window.initForms()
  initTextEllipsis()
  formatMarkdown()
  addPreCopyButtons()
  setFooterHeight()
  window.initDragSort()
  initCardGlow()

  window.MonacoEditorManager = new MonacoEditorManager()
  window.MonacoEditorManager.init()

  window.globalSettingsAssetsFileUploader.init()
  window.initPageSSE()

  let debounceTimeout: number

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      window.initSSE()
      window.initPageSSE()
    }
  })

  document.addEventListener("scroll", (event) => {
    if (!!(event.target as HTMLElement)?.tagName && (event.target as HTMLElement)?.closest(".dropdown")) {
      return
    }

    window.closeDropdowns()
  }, true)

  document.body.addEventListener("click", () => {
    window.closeDropdowns()
    window.togglePagesSidebar({ closed: true })
  })

  document.body.addEventListener("htmx:configRequest", (event) => {
    const details = (event as CustomEvent).detail
    const trigger = details.elt as HTMLElement
    const addTurnstileToken = trigger.hasAttribute("hx-confirm") && trigger.getAttribute("hx-confirm") === "turnstileVerification"

    if (addTurnstileToken) {
      (event as CustomEvent).detail.formData.append("cf-turnstile-response", window.turnstileToken.value)
      generateToken()
    }
  })

  document.body.addEventListener("htmx:historyRestore", () => {
    window.closeDropdowns()
  })

  document.body.addEventListener("htmx:beforeRequest", (event: CustomEvent) => {
    const headers = event.detail.requestConfig.headers
    if (!headers["HX-Keep-Dropdown"]) {
      window.closeDropdowns()
    }

    if (!headers["HX-Disable-Loader"] && !headers["HX-Preload"]) {
      loader.show()
    }
  })

  document.body.addEventListener("htmx:afterRequest", (_event) => {
    loader.hide()
  })

  window.addEventListener("htmx:afterSettle", () => {
    initTooltips()
    window.initForms()
    initTextEllipsis()
    formatMarkdown()
    addPreCopyButtons()
    setFooterHeight()
    window.initSSE()
    window.initPageSSE()
    restartSpinnersAnimations()
    window.initDragSort()
    window.MonacoEditorManager?.init()
    window.globalSettingsAssetsFileUploader.init()
    window.togglePagesSidebar({ closed: true })
    initCardGlow()
  })

  document.body.addEventListener("showSuccessToast", (event) => {
    const message = (event as CustomEvent).detail.value ?? "Operation completed successfully"
    window.toast.success(message)
  })

  document.body.addEventListener("showErrorToast", (event) => {
    const errorMessage = (event as CustomEvent).detail.value ?? "An unexpected error occurred, we're already working on it. Try again later or contact us"
    window.toast.error(errorMessage)
  })

  document.body.addEventListener("htmx:before-swap", (event) => {
    if ((event as CustomEvent).detail.xhr.status === 429) {
      (event as CustomEvent).detail.shouldSwap = false
      event.preventDefault()
      window.toast.error("Stai inviando richieste troppo velocemente. Riprova tra qualche secondo.")
    }
    
    const target = (event as CustomEvent).detail.target
    
    if (target && target.id === "modal") {
      const serverResponseText = (event as CustomEvent).detail.serverResponse
      if (!serverResponseText) {
        return
      }

      const parser = new DOMParser()
      const doc = parser.parseFromString(serverResponseText, "text/html")
      if (doc.querySelector("#turnstile-manual-modal")) {
        (event as CustomEvent).detail.isError = false;
        (event as CustomEvent).detail.shouldSwap = true   
      }
    }
  })

  document.body.addEventListener("closeModal", (event) => {
    const modalId = (event as CustomEvent).detail.value
    if (modalId) {
      window.closeModal(modalId)
    }
  })

  window.addEventListener("resize", () => {
    initTextEllipsis()
    setFooterHeight()
    clearTimeout(debounceTimeout)
    debounceTimeout = window.setTimeout(() => {
      window.onDropdownResize()
    }, BUTTON_DEBOUNCE)
  })

  window.setSidebarChatSectionScrollPosition()
})

window.addEventListener("popstate", () => {
  window.initForms()
  setFooterHeight()

  setTimeout(() => {
    initTextEllipsis()
    initTooltips()
    formatMarkdown()
    addPreCopyButtons()
    initCardGlow()
    window.globalSettingsAssetsFileUploader.init()
  }, 200)

  const id = setInterval(() => {
    if (loader.isReady()) {
      loader.hide()

      const buttons = document.querySelectorAll(".btn--loading,.suggestions__card--loading")
      buttons.forEach((button) => {
        button.classList.remove("btn--loading")
        button.classList.remove("suggestions__card--loading")
      })

      const prompt = document.getElementById("input-prompt-inner-container")
      if (prompt) {
        prompt.removeAttribute("disabled")
        prompt.dispatchEvent(new Event("input"))
      }

      clearInterval(id)
    }
  }, 20)
})

window.closeModal = (eventOrId: MouseEvent | string, remove = true) => {
  let modal: HTMLElement | null = null
  if (typeof eventOrId === "string") {
    modal = document.getElementById(eventOrId)
  } else {
    modal = (eventOrId.target as HTMLElement).closest(".modal__wrap")
  }

  if (modal) {
    if (remove) {
      modal.remove()
    } else {
      modal.classList.remove("is-open")
    }
  }
}

window.copyToClipboard = async (
  event: MouseEvent,
  value?: string,
  onSuccess?: (copiedValue: string) => void,
) => {
  event.preventDefault()
  event.stopPropagation()
  if (!value) {
    const target = event.target as HTMLElement
    value =
      target?.dataset?.["value"] ??
      (target
        ?.closest("[data-clipboard-value]")
        ?.getAttribute("data-clipboard-value") ||
        target
          ?.querySelector("[data-clipboard-value]")
          ?.getAttribute("data-clipboard-value") ||
        "")
  }
  try {
    await navigator.clipboard.writeText(value)
    onSuccess?.(value)
  } catch (_err) {
    window.toast.error("Failed to copy text to clipboard")
  }
}

const TOGGLE_COPY_ICON_TIMEOUT = 2000
window.toggleCopyIcon = (
  element: HTMLElement,
  timeout = TOGGLE_COPY_ICON_TIMEOUT,
  iconName = "copy"
) => {
  const tag = element.tagName === "use" ? element : element.querySelector("use")
  if (tag) {
    tag.setAttribute("href", "#check")
    setTimeout(() => {
      tag?.setAttribute("href", `#${iconName}`)
    }, timeout)
  }
}

window.showTemporaryTooltip = (anchor: HTMLElement, text: string, duration = 2000) => {
  const rect = anchor.getBoundingClientRect()
  const el = document.createElement("div")

  el.className = "tooltip tooltip--top tooltip--small"
  el.textContent = text

  document.getElementById("tooltip")?.appendChild(el)

  const tooltipWidth = el.getBoundingClientRect().width
  el.style.top = `${rect.top - 4}px`
  el.style.left = `${rect.left + rect.width / 2 - tooltipWidth / 2}px`

  requestAnimationFrame(() => {
    el.classList.add("tooltip--show")
  })

  setTimeout(() => {
    el.style.transition = "opacity 0.3s"
    el.style.opacity = "0"
    el.addEventListener("transitionend", () => el.remove(), { once: true })
  }, duration)
}

window.togglePasswordInput = (e: MouseEvent) => {
  const parent = (e.target as HTMLDivElement).closest(".input__container")
  parent
    ?.querySelector("input")
    ?.setAttribute(
      "type",
      parent.querySelector("input")?.getAttribute("type") === "password"
        ? "text"
        : "password",
    )
  const tag = (e.currentTarget as HTMLDivElement).querySelector("svg use")
  tag?.setAttribute(
    "href",
    tag.getAttribute("href") === "#eye" ? "#eye-slash" : "#eye",
  )
}
